import { z } from "zod";
import {
  ResumeUploadError,
  RESUME_UPLOAD_TTL_MS,
  validateResumeUpload,
} from "@/lib/domain/resume-upload";

const DATABASE = "landfast-resume-upload";
const STORE = "uploads";
const PENDING_KEY = "pending";
const metadataSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(255),
  expiresAt: z.number().int().positive(),
});

function withStore<T>(
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore, setResult: (value: T) => void) => void
): Promise<T> {
  return new Promise((resolve, reject) => {
    let database: IDBDatabase | undefined;
    let transaction: IDBTransaction | undefined;
    let result: T;
    let settled = false;
    const fail = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      database?.close();
      reject(new ResumeUploadError("HOME_RESUME_UPLOAD_STORAGE_ERROR"));
    };
    const timeout = setTimeout(() => {
      transaction?.abort();
      fail();
    }, 8_000);

    try {
      const request = indexedDB.open(DATABASE, 1);
      request.onupgradeneeded = () => request.result.createObjectStore(STORE);
      request.onerror = fail;
      request.onsuccess = () => {
        database = request.result;
        if (settled) {
          database.close();
          return;
        }
        transaction = database.transaction(STORE, mode);
        transaction.onabort = fail;
        transaction.onerror = fail;
        transaction.oncomplete = () => {
          settled = true;
          clearTimeout(timeout);
          database?.close();
          resolve(result);
        };
        try {
          operation(transaction.objectStore(STORE), (value) => { result = value; });
        } catch {
          transaction.abort();
          fail();
        }
      };
    } catch {
      fail();
    }
  });
}

export async function stageResumeUpload(file: File): Promise<string> {
  await validateResumeUpload(file);
  const id = crypto.randomUUID();
  await withStore<void>("readwrite", (store, setResult) => {
    // One pending document per browser keeps storage bounded, including abandoned sign-ins.
    store.put({ id, name: file.name, file, expiresAt: Date.now() + RESUME_UPLOAD_TTL_MS }, PENDING_KEY);
    setResult(undefined);
  });
  return id;
}

export async function removePendingResumeUpload(id: string): Promise<void> {
  await withStore<void>("readwrite", (store, setResult) => {
    const request = store.get(PENDING_KEY);
    request.onsuccess = () => {
      const metadata = metadataSchema.safeParse(request.result);
      // An older tab must not remove a newer tab's selected resume.
      if (metadata.success && metadata.data.id === id) store.delete(PENDING_KEY);
      setResult(undefined);
    };
  });
}

export async function readPendingResumeUpload(id: string): Promise<File> {
  const value = await withStore<unknown>("readonly", (store, setResult) => {
    const request = store.get(PENDING_KEY);
    request.onsuccess = () => setResult(request.result);
  });
  const metadata = metadataSchema.safeParse(value);
  if (!metadata.success || metadata.data.id !== id) {
    throw new ResumeUploadError("HOME_RESUME_UPLOAD_MISSING");
  }
  if (metadata.data.expiresAt <= Date.now()) {
    await removePendingResumeUpload(id);
    throw new ResumeUploadError("HOME_RESUME_UPLOAD_MISSING");
  }
  const blob = (value as { file?: unknown }).file;
  if (!(blob instanceof Blob)) {
    await removePendingResumeUpload(id);
    throw new ResumeUploadError("HOME_RESUME_UPLOAD_INVALID");
  }
  const file = new File([blob], metadata.data.name, { type: blob.type });
  await validateResumeUpload(file);
  return file;
}

export async function clearExpiredResumeUpload(): Promise<void> {
  await withStore<void>("readwrite", (store, setResult) => {
    const request = store.get(PENDING_KEY);
    request.onsuccess = () => {
      const metadata = metadataSchema.safeParse(request.result);
      if (!metadata.success || metadata.data.expiresAt <= Date.now()) store.delete(PENDING_KEY);
      setResult(undefined);
    };
  });
}
